import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ChainStatusBadge — additive, read-only indicator that the audit chain
 * for a given batch validates. Per the locked design (C1):
 *   - "OK"        → emerald "Chain ✓"
 *   - anything else → amber "Under review" (NEVER red — governance tone)
 *
 * Standalone fetch so it can be embedded anywhere without prop drilling.
 */
const ChainStatusBadge = ({ batchId, compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!batchId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/audit-chain/verify?batch_id=${batchId}`,
        );
        setStatus(r.data?.status || 'UNKNOWN');
      } catch {
        setStatus('UNKNOWN');
      } finally {
        setLoading(false);
      }
    })();
  }, [batchId]);

  if (loading) {
    return (
      <span
        data-testid={`chain-status-badge-loading-${batchId || 'na'}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-500 text-[10px] sm:text-xs font-medium"
      >
        <Loader2 className="w-3 h-3 animate-spin" /> Chain
      </span>
    );
  }
  const ok = status === 'OK';
  const empty = status === 'EMPTY';
  return (
    <span
      data-testid={`chain-status-badge-${batchId || 'na'}`}
      data-chain-status={status}
      title={
        ok
          ? 'Audit chain for this batch is unbroken and tamper-evident.'
          : empty
            ? 'No chained audit events for this batch yet.'
            : 'Audit chain anomaly detected — under review by compliance.'
      }
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium border whitespace-nowrap ${
        ok
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : empty
            ? 'bg-slate-50 border-slate-200 text-slate-500'
            : 'bg-amber-50 border-amber-300 text-amber-800'
      }`}
    >
      {ok ? <ShieldCheck className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
      {compact ? (ok ? '✓' : empty ? '—' : '!') : ok ? 'Chain ✓' : empty ? 'Chain —' : 'Under review'}
    </span>
  );
};

export default ChainStatusBadge;
